import { Button, Typography } from '@/components/atoms';
import { CheckIcon } from '@/components/atoms/icons/CheckIcon';
import { ChevronRightIcon } from '@/components/atoms/icons/ChevronRightIcon';
import { InfoCircleOutlineIcon } from '@/components/atoms/icons/InfoCircleOutlineIcon';
import { SearchIcon } from '@/components/atoms/icons/SearchIcon';
import { XIcon } from '@/components/atoms/icons/XIcon';
import { BottomSheet } from '@/components/molecules';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useTranslation, useUIColors } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { useI18nContext } from '@/core/providers/I18nProvider';
import type { AvailableUnit } from '@/core/api/models/properties';
import { useAppStore } from '@/core/stores/appStore';
import { OverlayScrollView } from '@/modules/properties-web/components/OverlayScrollView';
import {
    toAvailableUnit,
    useUnitNumberSearch,
    type SearchableUnit,
} from '@/modules/properties-web/hooks/useUnitNumberSearch';
import { formatPriceInMillions } from '@/modules/properties-web/utils/formatPrice';
import {
    BEDROOM_FILTER_OPTIONS,
    defaultSliderMillionsForBedrooms,
    resolveUnitBedroomOption,
    type BedroomFilterOption,
    type FilterableUnit,
} from '@/modules/properties-web/utils/unitListingFilters';
import React, { useEffect, useRef, useState } from 'react';
import {
    Platform,
    Pressable,
    ScrollView,
    StyleSheet,
    TextInput,
    useWindowDimensions,
    View,
} from 'react-native';
import { PriceRangeSlider } from './PriceRangeSlider';

/** Ignore tiny float drift when comparing the selected max price vs the sanctioned loan — same as web's properties-web PriceRangeSlider. */
const LOAN_COMPARE_EPSILON_AED = 500;
const MATCH_ROW_HEIGHT = 60;
const VISIBLE_MATCH_ROWS = 3;
const NO_LIMIT_PRICE_MILLIONS = 3;

export type CatalogueFilterUnit = FilterableUnit & {
    project_name?: string;
    unit_type?: string;
};

function resolveLoanSelectionRelation(
    selectionAed: number,
    loanAmountAed: number,
): 'above' | 'within' {
    if (selectionAed > loanAmountAed + LOAN_COMPARE_EPSILON_AED) {
        return 'above';
    }
    return 'within';
}

/** Splits a `{{placeholder}}`-templated string around the placeholder for inline colored spans. */
function splitTemplate(template: string, placeholder: string): { before: string; after: string } {
    const parts = template.split(placeholder);
    if (parts.length >= 2) {
        return { before: parts[0] ?? '', after: parts.slice(1).join(placeholder) };
    }
    return { before: template, after: '' };
}

function bedroomLabel(
    t: ReturnType<typeof useTranslation>['t'],
    option: BedroomFilterOption,
): string {
    return t('propertiesWeb.list.filters.bedroomsShort').replace(
        '{{count}}',
        option.replace('BR', ''),
    );
}

function unitSubtitle(
    t: ReturnType<typeof useTranslation>['t'],
    unit: CatalogueFilterUnit,
    projectName: string,
): string {
    const bedrooms = resolveUnitBedroomOption(unit);
    return [
        unit.project_name?.trim() || projectName,
        bedrooms ? bedroomLabel(t, bedrooms) : '',
        unit.unit_type?.trim() ?? '',
    ]
        .filter(Boolean)
        .join(' · ');
}

export interface PriceRangeFilterSheetProps {
    visible: boolean;
    onClose: () => void;
    /** Slider position in millions (1–3). 3 is the 3M+ end. */
    maxPrice: number;
    setMaxPrice: (value: number) => void;
    loanAmountAed: number;
    units: readonly AvailableUnit[];
    projectName: string;
    appliedBedrooms: readonly BedroomFilterOption[];
    onApplyBedrooms: (bedrooms: readonly BedroomFilterOption[]) => void;
    onUnitPress: (unitId: string) => void;
}

/**
 * Mobile catalogue Filters sheet. Price still commits only on Apply. Bedroom
 * chips and unit-number search match the listing filters.
 */
export function PriceRangeFilterSheet({
    visible,
    onClose,
    maxPrice,
    setMaxPrice,
    loanAmountAed,
    units,
    projectName,
    appliedBedrooms,
    onApplyBedrooms,
    onUnitPress,
}: Readonly<PriceRangeFilterSheetProps>) {
    const colors = useUIColors();
    const { t } = useTranslation();
    const { isRTL } = useI18nContext();
    const { height: windowHeight } = useWindowDimensions();
    const pendingUnitIdRef = useRef<string | null>(null);
    const upsertAvailableUnitFirst = useAppStore((state) => state.upsertAvailableUnitFirst);

    const [stagedMaxPrice, setStagedMaxPrice] = useState(maxPrice);
    const [query, setQuery] = useState('');
    const [draftBedrooms, setDraftBedrooms] =
        useState<readonly BedroomFilterOption[]>(appliedBedrooms);

    useEffect(() => {
        if (visible) {
            setStagedMaxPrice(maxPrice);
            setDraftBedrooms(appliedBedrooms);
            setQuery('');
            trackCatalogEvent({
                screen: ALMETLAA_SCREEN.CATALOGUE,
                actionName: ALMETLAA_ACTION.CATALOGUE.PRICE_FILTER_OPEN,
                eventType: 'action',
            });
        }
    }, [visible, maxPrice, appliedBedrooms]);

    const { matches, isSearchingRemote } = useUnitNumberSearch(query, units);

    const loanDisplayValue = formatPriceInMillions(loanAmountAed, isRTL);
    const selectionAed = Math.min(stagedMaxPrice, NO_LIMIT_PRICE_MILLIONS) * 1_000_000;
    const isAboveLoan =
        resolveLoanSelectionRelation(
            selectionAed,
            Number.isFinite(loanAmountAed) ? Math.max(0, loanAmountAed) : 0,
        ) === 'above';
    const loanStatusLabel = isAboveLoan ? t('properties.above') : t('properties.within');
    const loanStatusColor = isAboveLoan ? UI_PRIMITIVES.redDefault : UI_PRIMITIVES.greenMedium;

    const rawLoanNoteTemplate = t('properties.availableLoanAmountNote');
    const rawEligibilityTemplate = t('properties.loanEligibility');
    const { before: loanNoteBefore, after: loanNoteAfter } = splitTemplate(
        rawLoanNoteTemplate,
        '{{loanAmount}}',
    );
    const { before: eligibilityBefore, after: eligibilityAfter } = splitTemplate(
        rawEligibilityTemplate,
        '{{loanStatus}}',
    );

    const commitAndClose = (bedrooms: readonly BedroomFilterOption[], priceMillions: number) => {
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.CATALOGUE,
            actionName: ALMETLAA_ACTION.CATALOGUE.PRICE_FILTER_APPLY,
            eventType: 'action',
        });
        trackCatalogEvent({
            screen: ALMETLAA_SCREEN.CATALOGUE,
            actionName: ALMETLAA_ACTION.CATALOGUE.FILTER_APPLY_CLICK,
            eventType: 'action',
            properties: { p_sectionName: String(priceMillions) },
        });
        setMaxPrice(priceMillions);
        onApplyBedrooms(bedrooms);
        onClose();
    };

    const openMatchedUnit = (unit: SearchableUnit) => {
        const availableUnit = toAvailableUnit(unit);
        if (availableUnit) {
            upsertAvailableUnitFirst(availableUnit);
        }
        pendingUnitIdRef.current = unit.unit_id;
        onClose();
        if (Platform.OS === 'ios') {
            return;
        }
        pendingUnitIdRef.current = null;
        onUnitPress(unit.unit_id);
    };

    return (
        <BottomSheet
            visible={visible}
            onClose={onClose}
            showDragHandle={false}
            onDismiss={() => {
                const unitId = pendingUnitIdRef.current;
                pendingUnitIdRef.current = null;
                if (unitId) {
                    onUnitPress(unitId);
                }
            }}
        >
            <View style={styles.header}>
                <View style={styles.headerSide} />
                <Typography variant="title2" color="primary" style={styles.title}>
                    {t('propertiesWeb.list.filters.title')}
                </Typography>
                <Pressable
                    onPress={onClose}
                    accessibilityRole="button"
                    accessibilityLabel={t('propertiesWeb.list.filters.close')}
                    style={[styles.closeButton, { backgroundColor: colors.backgroundLevel2 }]}
                >
                    <XIcon size={18} color={colors.fontPrimary} />
                </Pressable>
            </View>

            <ScrollView
                style={{ maxHeight: windowHeight * 0.62 }}
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
                nestedScrollEnabled
                showsVerticalScrollIndicator
            >
                <View style={styles.section}>
                    <Typography variant="headline" color="primary">
                        {t('properties.priceRange')}
                    </Typography>
                    <View
                        style={[
                            styles.banner,
                            {
                                backgroundColor: colors.backgroundLevel2,
                                borderColor: colors.outlineInactive,
                            },
                        ]}
                    >
                        <InfoCircleOutlineIcon size={14} color="#A0A0B0" />
                        <Typography variant="footnote" color="secondary" style={styles.bannerText}>
                            {loanNoteBefore}
                            <Typography variant="footnote" style={styles.loanAmountText}>
                                {`AED ${loanDisplayValue}`}
                            </Typography>
                            {loanNoteAfter}
                            {'\n'}
                            {eligibilityBefore}
                            <Typography
                                variant="footnote"
                                style={[styles.statusLabel, { color: loanStatusColor }]}
                            >
                                {loanStatusLabel}
                            </Typography>
                            {eligibilityAfter}
                        </Typography>
                    </View>
                    <PriceRangeSlider
                        min={1}
                        max={NO_LIMIT_PRICE_MILLIONS}
                        value={stagedMaxPrice}
                        ticks={[1.5, 2, 2.5]}
                        loanAmountAed={loanAmountAed}
                        onValueChange={(v) => setStagedMaxPrice(v as number)}
                    />
                </View>

                <UnitNumberSection
                    colors={colors}
                    query={query}
                    onQueryChange={setQuery}
                    matches={matches}
                    isSearchingRemote={isSearchingRemote}
                    projectName={projectName}
                    isRTL={isRTL}
                    onUnitPress={openMatchedUnit}
                />

                <BedroomSection
                    colors={colors}
                    selected={draftBedrooms}
                    onToggle={(option) => {
                        setDraftBedrooms((current) => {
                            const next = current.includes(option)
                                ? current.filter((value) => value !== option)
                                : [...current, option];
                            setStagedMaxPrice(
                                defaultSliderMillionsForBedrooms(next, NO_LIMIT_PRICE_MILLIONS),
                            );
                            return next;
                        });
                    }}
                />
            </ScrollView>

            <View style={styles.footer}>
                <Pressable
                    onPress={() => commitAndClose([], NO_LIMIT_PRICE_MILLIONS)}
                    accessibilityRole="button"
                    accessibilityLabel={t('propertiesWeb.list.filters.clearAll')}
                    style={styles.clearAll}
                >
                    <Typography variant="headline" color="primary" style={styles.clearAllLabel}>
                        {t('propertiesWeb.list.filters.clearAll')}
                    </Typography>
                </Pressable>
                <Button
                    title={t('propertiesWeb.list.filters.applyFilter')}
                    variant="primary"
                    style={styles.applyButton}
                    onPress={() => commitAndClose(draftBedrooms, stagedMaxPrice)}
                />
            </View>
        </BottomSheet>
    );
}

function UnitNumberSection({
    colors,
    query,
    onQueryChange,
    matches,
    isSearchingRemote,
    projectName,
    isRTL,
    onUnitPress,
}: Readonly<{
    colors: ReturnType<typeof useUIColors>;
    query: string;
    onQueryChange: (value: string) => void;
    matches: readonly SearchableUnit[];
    isSearchingRemote: boolean;
    projectName: string;
    isRTL: boolean;
    onUnitPress: (unit: SearchableUnit) => void;
}>) {
    const { t } = useTranslation();
    const hasQuery = query.trim().length > 0;

    return (
        <View style={styles.section}>
            <Typography variant="headline" color="primary">
                {t('propertiesWeb.list.filters.unitNumber')}
            </Typography>
            <View
                style={[
                    styles.searchField,
                    {
                        flexDirection: isRTL ? 'row-reverse' : 'row',
                        borderColor: hasQuery ? colors.bloomRed : colors.outlineInactive,
                        backgroundColor: colors.backgroundLevel1,
                    },
                ]}
            >
                <SearchIcon size={18} color={colors.fontTertiary} />
                <TextInput
                    value={query}
                    onChangeText={onQueryChange}
                    placeholder={t('propertiesWeb.list.filters.unitNumberPlaceholder')}
                    placeholderTextColor={UI_PRIMITIVES.greyWarm625C59}
                    accessibilityLabel={t('propertiesWeb.list.filters.unitNumber')}
                    autoCorrect={false}
                    autoCapitalize="characters"
                    style={[
                        styles.searchInput,
                        {
                            color: colors.fontPrimary,
                            textAlign: isRTL ? 'right' : 'left',
                            writingDirection: isRTL ? 'rtl' : 'ltr',
                        },
                    ]}
                />
            </View>
            {hasQuery ? (
                <UnitMatchesList
                    colors={colors}
                    matches={matches}
                    isSearchingRemote={isSearchingRemote}
                    projectName={projectName}
                    isRTL={isRTL}
                    onUnitPress={onUnitPress}
                />
            ) : null}
        </View>
    );
}

function UnitMatchesList({
    colors,
    matches,
    isSearchingRemote,
    projectName,
    isRTL,
    onUnitPress,
}: Readonly<{
    colors: ReturnType<typeof useUIColors>;
    matches: readonly SearchableUnit[];
    isSearchingRemote: boolean;
    projectName: string;
    isRTL: boolean;
    onUnitPress: (unit: SearchableUnit) => void;
}>) {
    const { t } = useTranslation();

    return (
        <View
            style={[
                styles.matches,
                { borderColor: colors.outlineInactive, backgroundColor: colors.backgroundLevel1 },
            ]}
        >
            <View style={[styles.matchesHeader, { backgroundColor: colors.backgroundLevel2 }]}>
                <Typography variant="caption" color="tertiary">
                    {matches.length > 0 || isSearchingRemote
                        ? t('propertiesWeb.list.filters.matchingUnits')
                        : t('propertiesWeb.list.filters.noMatchingUnits')}
                </Typography>
            </View>
            <OverlayScrollView style={styles.matchesScroll} nestedScrollEnabled>
                {matches.map((unit) => (
                    <Pressable
                        key={unit.unit_id}
                        onPress={() => onUnitPress(unit)}
                        accessibilityRole="button"
                        accessibilityLabel={unit.unit_number}
                        style={[styles.matchRow, { borderTopColor: colors.outlineInactive }]}
                    >
                        <View style={styles.matchText}>
                            <Typography variant="headline" color="primary">
                                {unit.unit_number}
                            </Typography>
                            <Typography variant="caption" color="tertiary">
                                {unitSubtitle(t, unit, projectName)}
                            </Typography>
                        </View>
                        <View style={{ transform: [{ scaleX: isRTL ? -1 : 1 }] }}>
                            <ChevronRightIcon size={16} color={colors.fontPrimary} />
                        </View>
                    </Pressable>
                ))}
            </OverlayScrollView>
        </View>
    );
}

function BedroomSection({
    colors,
    selected,
    onToggle,
}: Readonly<{
    colors: ReturnType<typeof useUIColors>;
    selected: readonly BedroomFilterOption[];
    onToggle: (option: BedroomFilterOption) => void;
}>) {
    const { t } = useTranslation();

    return (
        <View style={styles.section}>
            <View style={styles.bedroomHeading}>
                <Typography variant="headline" color="primary">
                    {t('propertiesWeb.list.filters.numberOfBedrooms')}
                </Typography>
                <Typography variant="footnote" color="tertiary">
                    {t('propertiesWeb.list.filters.bedroomsHint')}
                </Typography>
            </View>
            <View style={styles.chips}>
                {BEDROOM_FILTER_OPTIONS.map((option) => {
                    const isSelected = selected.includes(option);
                    return (
                        <Pressable
                            key={option}
                            onPress={() => onToggle(option)}
                            accessibilityRole="checkbox"
                            accessibilityState={{ checked: isSelected }}
                            style={[
                                styles.chip,
                                {
                                    borderColor: isSelected
                                        ? UI_PRIMITIVES.redLight
                                        : colors.outlineInactive,
                                    backgroundColor: isSelected
                                        ? UI_PRIMITIVES.redPaleF5EAE8
                                        : colors.backgroundLevel1,
                                },
                            ]}
                        >
                            {isSelected ? (
                                <View style={[styles.chipCheck, { backgroundColor: UI_PRIMITIVES.redLight }]}>
                                    <CheckIcon size={10} color={colors.white} />
                                </View>
                            ) : null}
                            <Typography variant="subhead" color="primary">
                                {bedroomLabel(t, option)}
                            </Typography>
                        </Pressable>
                    );
                })}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 4,
    },
    headerSide: {
        width: 36,
    },
    title: {
        flex: 1,
        textAlign: 'center',
        fontWeight: '700',
        fontSize: 20,
    },
    closeButton: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center',
    },
    scrollContent: {
        gap: 20,
        paddingBottom: 8,
    },
    section: {
        gap: 12,
    },
    banner: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        borderRadius: 12,
        borderWidth: 1,
        padding: 12,
    },
    bannerText: {
        flex: 1,
        lineHeight: 18,
    },
    statusLabel: {
        fontFamily: 'Matter-Regular',
        fontWeight: '400',
        fontSize: 12,
        lineHeight: 19.38,
        letterSpacing: 0,
    },
    loanAmountText: {
        color: UI_PRIMITIVES.redLight,
        fontFamily: 'Matter-Medium',
        fontWeight: '400',
        fontSize: 14,
        lineHeight: 19.38,
        letterSpacing: 0,
    },
    searchField: {
        alignItems: 'center',
        gap: 8,
        minHeight: 48,
        paddingHorizontal: 12,
        borderRadius: 8,
        borderWidth: 1,
    },
    searchInput: {
        flex: 1,
        minHeight: 46,
    },
    matches: {
        borderWidth: 1,
        borderRadius: 8,
        overflow: 'hidden',
    },
    matchesHeader: {
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    matchesScroll: {
        maxHeight: MATCH_ROW_HEIGHT * VISIBLE_MATCH_ROWS,
    },
    matchRow: {
        height: MATCH_ROW_HEIGHT,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        paddingHorizontal: 12,
        borderTopWidth: 1,
    },
    matchText: {
        flex: 1,
        gap: 2,
    },
    bedroomHeading: {
        gap: 2,
    },
    chips: {
        flexDirection: 'row',
        gap: 8,
    },
    chip: {
        flex: 1,
        flexDirection: 'row',
        gap: 6,
        minHeight: 40,
        borderRadius: 8,
        borderWidth: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    chipCheck: {
        width: 18,
        height: 18,
        borderRadius: 9,
        alignItems: 'center',
        justifyContent: 'center',
    },
    footer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 16,
        marginTop: 4,
    },
    clearAll: {
        paddingVertical: 12,
        paddingHorizontal: 4,
    },
    clearAllLabel: {
        textDecorationLine: 'underline',
    },
    applyButton: {
        flex: 1,
    },
});

export default PriceRangeFilterSheet;
